import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { isWrmOptimizationsEnabled, isUnifiedHelpReportingPageEnabled } from 'feature-flags';
import { ForgeActionModule } from 'view/forge-ui/forge-action';
import * as grid from 'view/styles/grid';
import { AnalyticsContext } from '@atlaskit/analytics-next';
import DropdownMenu, { DropdownItemGroup, DropdownItem as AtlasKitDropdownItem } from '@atlaskit/dropdown-menu';
import { token } from '@atlaskit/tokens';
import { DropdownItem } from '@atlassian/help-center-common-component/analytics';
import { initializeConnect } from '@atlassian/help-center-common-component/connect-fragment';
import {
    USER_MENU_ACTION_COMPONENT,
    USER_MENU_ACTION_SECTION_MODULE,
} from '@atlassian/help-center-common-component/forge-ui/constants';
import { ForgeModalContext } from '@atlassian/help-center-common-component/forge-ui/modal-context';
import { ForgeModalContextProvider } from '@atlassian/help-center-common-component/forge-ui/modal-context-provider';
import type { SetExtensionToDisplayType } from '@atlassian/help-center-common-component/forge-ui/types';
import { InteractiveElement } from '@atlassian/help-center-common-component/interactive-element';
import { withDeferredWrmLoaded } from '@atlassian/help-center-common-util/wrm';
import { NoUnderlineLink } from '../styled';
import { UserAvatar } from '../user-avatar';
import i18n from './messages';

export interface CustomMenuLinkProps {
    id: string | undefined;
    key: string | undefined;
    label: string | undefined;
    href: string;
    styleClass: string | undefined;
}

export interface StateProps {
    avatarSrc: string | undefined;
    customMenuLinks: CustomMenuLinkProps[];
    canAdministerJira: boolean | undefined;
}

export interface DispatchProps {
    onLogoutClick: AnyFunction;
    onOpenAndClose?: (e: { isOpen: boolean }) => void;
}

export type Props = StateProps & DispatchProps;

// FSD-2327: introduce a way to extend classnames nicely
const EXCLUDE_CUSTOMISATION_CLASS = 'cv-exclude-from-customisation';

const profileLink = () => {
    return (
        <NoUnderlineLink data-test-id="profile-link" to="/user/profile" actionSubjectId="navbarProfile">
            <AtlasKitDropdownItem className={EXCLUDE_CUSTOMISATION_CLASS}>
                <FormattedMessage {...i18n.profile} />
            </AtlasKitDropdownItem>
        </NoUnderlineLink>
    );
};

const reportingMenuLink = () => {
    return (
        <NoUnderlineLink to="/reporting" actionSubjectId="navbarReporting">
            <AtlasKitDropdownItem className={EXCLUDE_CUSTOMISATION_CLASS}>
                <FormattedMessage {...i18n.reporting} />
            </AtlasKitDropdownItem>
        </NoUnderlineLink>
    );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const logoutLink = (onLogoutClick: () => any) => (
    <AnalyticsContext data={{ actionSubjectId: 'navbarLogout' }}>
        <DropdownItem data-test-id="logout-link" onClick={onLogoutClick} className={EXCLUDE_CUSTOMISATION_CLASS}>
            <FormattedMessage {...i18n.logout} />
        </DropdownItem>
    </AnalyticsContext>
);

const buildCustomMenuLinks = (customMenuLinks: CustomMenuLinkProps[]) => {
    return customMenuLinks.map((menuLink) => (
        <DropdownItem
            data-test-id="custom-menu-link"
            id={menuLink.id}
            href={menuLink.href}
            key={menuLink.key}
            // TypeScript upgrade (v4.4.3). Please correct when you revisit this code. Please correct when this code is revisited.
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            className={`${EXCLUDE_CUSTOMISATION_CLASS} ${menuLink.styleClass}`}
        >
            {menuLink.label}
        </DropdownItem>
    ));
};

export const CustomMenuLinks = ({ customMenuLinks }: { customMenuLinks: CustomMenuLinkProps[] }) => (
    <span data-test-id="custom-menu-link">
        {customMenuLinks.map((menuLink) => (
            <DropdownItem
                id={menuLink.id}
                href={menuLink.href}
                key={menuLink.key}
                // TypeScript upgrade (v4.4.3). Please correct when you revisit this code. Please correct when this code is revisited.
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                className={`${EXCLUDE_CUSTOMISATION_CLASS} ${menuLink.styleClass}`}
            >
                {menuLink.label}
            </DropdownItem>
        ))}
    </span>
);

const UserProfileConnectActionLinks = withDeferredWrmLoaded(CustomMenuLinks, initializeConnect);

const forgeActionLinkOnClick = (id: string, onClick: SetExtensionToDisplayType) => () => onClick(id);

export const ForgeTitleMenuLinks = () => {
    const {
        data: { extensionTitles },
        methods: { setExtensionToDisplay: onClick },
    } = React.useContext(ForgeModalContext);

    return (
        <>
            {extensionTitles.map(({ id, title }) => {
                return (
                    <DropdownItem
                        id={id}
                        key={id}
                        className={`${EXCLUDE_CUSTOMISATION_CLASS}`}
                        onClick={forgeActionLinkOnClick(id, onClick)}
                    >
                        {title}
                    </DropdownItem>
                );
            })}
        </>
    );
};

// TODO: Remove State when ticket AK-4467 is fixed
interface State {
    isOpen: boolean;
}

export default class AvatarMenu extends React.Component<Props, State> {
    static defaultProps = {
        customMenuLinks: [],
    };

    state: State = {
        isOpen: false,
    };

    onItemClick = () => {
        this.setState({ isOpen: false });
    };

    onOpenChange = (e: { isOpen: boolean }) => {
        if (!!this.props.onOpenAndClose) {
            this.props.onOpenAndClose(e);
        }
        this.setState({ isOpen: e.isOpen });
    };

    renderDropdownMenu = () => {
        const { avatarSrc, onLogoutClick, customMenuLinks, canAdministerJira } = this.props;
        return (
            <DropdownMenu
                shouldFlip={false}
                appearance="tall"
                trigger={<UserAvatar avatarSrc={avatarSrc} isExpanded={this.state.isOpen} />}
                position="bottom right"
                onOpenChange={this.onOpenChange}
                isOpen={this.state.isOpen}
            >
                <DropdownItemGroup title={<FormattedMessage {...i18n.account} />}>
                    <InteractiveElement
                        tagName="span"
                        onClick={this.onItemClick}
                        data-test-id="menu-wrapper"
                        tabIndex={-1}
                    >
                        {profileLink()}
                        {/* eslint-disable-next-line no-nested-ternary */}
                        {customMenuLinks && customMenuLinks.length > 0 ? (
                            isWrmOptimizationsEnabled() ? (
                                <UserProfileConnectActionLinks customMenuLinks={customMenuLinks} />
                            ) : (
                                buildCustomMenuLinks(customMenuLinks)
                            )
                        ) : undefined}
                        <ForgeTitleMenuLinks />
                        {isUnifiedHelpReportingPageEnabled() && canAdministerJira ? reportingMenuLink() : null}
                        {logoutLink(onLogoutClick)}
                    </InteractiveElement>
                </DropdownItemGroup>
            </DropdownMenu>
        );
    };

    render() {
        return (
            <AvatarMenuContainer>
                <ForgeModalContextProvider type={USER_MENU_ACTION_SECTION_MODULE}>
                    {this.renderDropdownMenu()}
                    <ForgeActionModule
                        type={USER_MENU_ACTION_SECTION_MODULE}
                        componentName={USER_MENU_ACTION_COMPONENT}
                    />
                </ForgeModalContextProvider>
            </AvatarMenuContainer>
        );
    }
}

const AvatarMenuContainer = styled.div`
    padding: 0 ${token('space.100', '8px')};
    /*
        Because the avatar component sets multiple levels
        of divs with display: flex or display: inline-flex,
        to get the right height we need to force it here.
     */
    height: ${grid.multiple(4.5).px};
`;
